import supabase from '../config/supabase';
import config from '../config/environment';
import type { PhotoPosition } from '../types';
import { fileToBase64DataUrl } from '../utils/imageResize';
import { handleServiceError } from '../utils/errorHandler';

export type StudentPhotoUploadType = 'body' | 'profile' | 'equipment';

export interface UploadStudentPhotoParams {
  studentId: number;
  photoType: StudentPhotoUploadType;
  file: File;
  position?: PhotoPosition;
  studentToken?: string;
  assessmentDate?: string;
}

export interface UploadEquipmentPhotosParams {
  studentId: number;
  files: File[];
  studentToken?: string;
}

export interface DeleteStudentPhotoParams {
  studentId: number;
  photoType: Exclude<StudentPhotoUploadType, 'equipment'>;
  position?: PhotoPosition;
  studentToken?: string;
  assessmentDate?: string;
}

interface UploadStudentPhotoResponse {
  success: boolean;
  error?: string;
  data?: {
    storagePath: string;
    signedUrl?: string;
    publicUrl?: string;
    uploads?: Array<{ storagePath: string; signedUrl: string }>;
  };
}

export interface ListStudentBodyPhotosParams {
  studentId: number;
  studentToken?: string;
}

export interface ListedBodyPhotoAssessment {
  assessmentDate: string;
  photos: Array<{
    position: PhotoPosition;
    url: string;
    uploadedAt: string;
    storagePath?: string;
  }>;
}

export interface ListedEquipmentPhoto {
  fileName: string;
  storagePath: string;
  url: string;
  uploadedAt: string | null;
}

export interface ListEquipmentPhotosResult {
  photos: ListedEquipmentPhoto[];
  count: number;
  alreadyReceived: boolean;
}

class StudentPhotoUploadServiceClass {
  private async getAuthHeaders(studentToken?: string): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      apikey: config.supabaseAnonKey,
    };

    if (studentToken) {
      return headers;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    return headers;
  }

  async listBodyPhotoAssessments(
    params: ListStudentBodyPhotosParams,
  ): Promise<ListedBodyPhotoAssessment[]> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'listar fotos');
    }

    const headers = await this.getAuthHeaders(params.studentToken);

    const response = await fetch(`${config.supabaseFunctionsUrl}/upload-student-photo`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'list-body-photos',
        studentId: params.studentId,
        studentToken: params.studentToken,
      }),
    });

    const payload = await response.json().catch(() => ({})) as {
      success?: boolean;
      error?: string;
      data?: { assessments?: ListedBodyPhotoAssessment[] };
    };

    if (!response.ok || !payload.success) {
      handleServiceError(
        new Error(payload.error || 'Erro ao listar fotos.'),
        'listar fotos',
      );
    }

    return Array.isArray(payload.data?.assessments) ? payload.data.assessments : [];
  }

  async listEquipmentPhotos(
    params: ListStudentBodyPhotosParams,
  ): Promise<ListEquipmentPhotosResult> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'listar fotos de equipamentos');
    }

    const headers = await this.getAuthHeaders(params.studentToken);

    const response = await fetch(`${config.supabaseFunctionsUrl}/upload-student-photo`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'list-equipment-photos',
        studentId: params.studentId,
        studentToken: params.studentToken,
      }),
    });

    const payload = await response.json().catch(() => ({})) as {
      success?: boolean;
      error?: string;
      data?: ListEquipmentPhotosResult;
    };

    if (!response.ok || !payload.success) {
      handleServiceError(
        new Error(payload.error || 'Erro ao listar fotos de equipamentos.'),
        'listar fotos de equipamentos',
      );
    }

    return {
      photos: Array.isArray(payload.data?.photos) ? payload.data.photos : [],
      count: typeof payload.data?.count === 'number' ? payload.data.count : 0,
      alreadyReceived: Boolean(payload.data?.alreadyReceived),
    };
  }

  async uploadPhoto(params: UploadStudentPhotoParams): Promise<string> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'enviar foto');
    }

    const imageBase64 = await fileToBase64DataUrl(params.file);
    const headers = await this.getAuthHeaders(params.studentToken);

    const response = await fetch(`${config.supabaseFunctionsUrl}/upload-student-photo`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        studentId: params.studentId,
        photoType: params.photoType,
        position: params.photoType === 'body' ? params.position : undefined,
        imageBase64,
        studentToken: params.studentToken,
        assessmentDate: params.assessmentDate,
      }),
    });

    const payload = await response.json().catch(() => ({})) as UploadStudentPhotoResponse;
    const signedUrl = payload.data?.signedUrl || payload.data?.publicUrl;

    if (!response.ok || !payload.success || !signedUrl) {
      handleServiceError(
        new Error(payload.error || 'Erro ao enviar foto.'),
        'enviar foto',
      );
    }

    return signedUrl!;
  }

  async uploadEquipmentPhotos(params: UploadEquipmentPhotosParams): Promise<string[]> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'enviar fotos de equipamentos');
    }

    if (params.files.length === 0) {
      return [];
    }

    const headers = await this.getAuthHeaders(params.studentToken);
    const signedUrls: string[] = [];

    // Envia em sequência para evitar payload gigante (até 20 imagens).
    for (const file of params.files) {
      const imageBase64 = await fileToBase64DataUrl(file);

      const response = await fetch(`${config.supabaseFunctionsUrl}/upload-student-photo`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          studentId: params.studentId,
          photoType: 'equipment',
          imageBase64,
          studentToken: params.studentToken,
        }),
      });

      const payload = await response.json().catch(() => ({})) as UploadStudentPhotoResponse;

      if (!response.ok || !payload.success) {
        handleServiceError(
          new Error(payload.error || 'Erro ao enviar fotos de equipamentos.'),
          'enviar fotos de equipamentos',
        );
      }

      const signedUrl = payload.data?.signedUrl || payload.data?.publicUrl;
      if (signedUrl) {
        signedUrls.push(signedUrl);
      }
    }

    if (params.studentToken) {
      await this.completePhotoUploadLink({
        studentId: params.studentId,
        studentToken: params.studentToken,
      });
    }

    return signedUrls;
  }

  async completePhotoUploadLink(params: {
    studentId: number;
    studentToken: string;
  }): Promise<void> {
    if (!config.supabaseFunctionsUrl || !params.studentToken.trim()) {
      return;
    }

    const headers = await this.getAuthHeaders(params.studentToken);

    const response = await fetch(`${config.supabaseFunctionsUrl}/upload-student-photo`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'complete-photo-upload-link',
        studentId: params.studentId,
        studentToken: params.studentToken,
      }),
    });

    const payload = await response.json().catch(() => ({})) as UploadStudentPhotoResponse;

    if (!response.ok || !payload.success) {
      // Não bloqueia o fluxo após upload bem-sucedido.
      console.warn('[completePhotoUploadLink]', payload.error || response.statusText);
    }
  }

  async deletePhoto(params: DeleteStudentPhotoParams): Promise<void> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'remover foto');
    }

    const headers = await this.getAuthHeaders(params.studentToken);

    const response = await fetch(`${config.supabaseFunctionsUrl}/upload-student-photo`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'delete',
        studentId: params.studentId,
        photoType: params.photoType,
        position: params.photoType === 'body' ? params.position : undefined,
        studentToken: params.studentToken,
        assessmentDate: params.assessmentDate,
      }),
    });

    const payload = await response.json().catch(() => ({})) as UploadStudentPhotoResponse;

    if (!response.ok || !payload.success) {
      handleServiceError(
        new Error(payload.error || 'Erro ao remover foto.'),
        'remover foto',
      );
    }
  }
}

export const StudentPhotoUploadService = new StudentPhotoUploadServiceClass();
